import { useCallback, useEffect, useMemo, useState } from "react";

import { DateTimeDisplay } from "../../components/date-time-display";
import { EmptyState, ErrorNotice, LoadingView } from "../../components/feedback";
import { requestAdminApi } from "../../lib/api-client";
import type { PushCampaign, PushCampaignList } from "../../types";
import { formatCampaignStatus } from "./push-display";

type PushHistoryScreenProps = {
  onCreate: () => void;
  onOpenCampaign: (campaignId: string) => void;
};

/**
 * お知らせ配信の履歴一覧画面
 *
 * @param props.onCreate - 新規作成画面を開く処理
 * @param props.onOpenCampaign - 配信詳細画面を開く処理
 * @returns お知らせ配信の履歴一覧画面
 */
export const PushHistoryScreen = ({ onCreate, onOpenCampaign }: PushHistoryScreenProps) => {
  // 配信履歴を保持する
  const [campaigns, setCampaigns] = useState<PushCampaign[]>([]);

  // 履歴読み込み中かを保持する
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  // 履歴取得のエラーを保持する
  const [historyError, setHistoryError] = useState<string | null>(null);

  // 配信履歴を読み込む
  const loadCampaigns = useCallback(async () => {
    setIsLoadingHistory(true);
    setHistoryError(null);

    try {
      const result = await requestAdminApi<PushCampaignList>("/v1/admin/push-campaigns");
      setCampaigns(result.campaigns);
    } catch (error: unknown) {
      console.error("Failed to load push campaigns", error);
      setHistoryError("配信履歴を読み込めませんでした。");
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // 配信履歴の集計値を算出する
  const campaignSummary = useMemo(() => {
    return campaigns.reduce(
      (summary, campaign) => {
        return {
          sent: summary.sent + campaign.sent_count,
          failed: summary.failed + campaign.failed_count,
        };
      },
      { sent: 0, failed: 0 },
    );
  }, [campaigns]);

  // 初回表示時に配信履歴を読み込む
  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">お知らせ管理</p>
          <h1>お知らせ配信</h1>
          <p>これまでのお知らせと配信結果を確認できます。</p>
        </div>
        <button className="button button--primary" type="button" onClick={onCreate}>
          新しい配信を作成
        </button>
      </section>

      <div className="push-history-layout">
        <section className="stats-grid" aria-label="配信集計">
          <div className="stat-card">
            <span>配信数</span>
            <strong>{campaigns.length.toLocaleString("ja-JP")}</strong>
          </div>
          <div className="stat-card">
            <span>送信成功</span>
            <strong>{campaignSummary.sent.toLocaleString("ja-JP")}</strong>
          </div>
          <div className="stat-card stat-card--warning">
            <span>送信失敗</span>
            <strong>{campaignSummary.failed.toLocaleString("ja-JP")}</strong>
          </div>
        </section>

        <section
          className="panel history-panel history-panel--standalone"
          aria-labelledby="history-title"
        >
          <div className="panel__header">
            <div>
              <p className="eyebrow">配信記録</p>
              <h2 id="history-title">配信履歴</h2>
            </div>
            <button
              className="text-button"
              type="button"
              disabled={isLoadingHistory}
              onClick={() => void loadCampaigns()}
            >
              {isLoadingHistory ? "更新中…" : "更新"}
            </button>
          </div>

          {historyError ? (
            <ErrorNotice message={historyError} onRetry={() => void loadCampaigns()} />
          ) : null}
          {isLoadingHistory ? (
            <LoadingView label="配信履歴を読み込んでいます" />
          ) : campaigns.length === 0 ? (
            <EmptyState
              title="配信履歴はありません"
              description="「新しい配信を作成」から最初のお知らせを作成できます。"
            />
          ) : (
            <div className="table-scroll">
              <table className="campaign-history-table">
                <thead>
                  <tr>
                    <th scope="col">配信日時</th>
                    <th scope="col">内容</th>
                    <th scope="col">状態</th>
                    <th scope="col">対象端末</th>
                    <th scope="col">成功</th>
                    <th scope="col">失敗</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr
                      key={campaign.campaign_id}
                      className="campaign-history-table__row"
                      tabIndex={0}
                      aria-label={`${campaign.localizations.ja.title || "お知らせ"}の配信詳細を開く`}
                      onClick={() => onOpenCampaign(campaign.campaign_id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onOpenCampaign(campaign.campaign_id);
                        }
                      }}
                    >
                      <td>
                        <DateTimeDisplay value={campaign.created_at} />
                      </td>
                      <td>
                        <div className="campaign-history-table__content">
                          <strong>
                            {campaign.localizations.ja.title || "日本語タイトル未入力"}
                          </strong>
                          <span>{campaign.localizations.ja.body || "日本語本文未入力"}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge--${campaign.status}`}>
                          {formatCampaignStatus(campaign.status)}
                        </span>
                      </td>
                      <td className="campaign-history-table__number">
                        {campaign.target_installation_count.toLocaleString("ja-JP")}
                      </td>
                      <td className="campaign-history-table__number">
                        {campaign.sent_count.toLocaleString("ja-JP")}
                      </td>
                      <td className="campaign-history-table__number">
                        <span>{campaign.failed_count.toLocaleString("ja-JP")}</span>
                        {campaign.invalid_token_count > 0 ? (
                          <small>
                            無効 {campaign.invalid_token_count.toLocaleString("ja-JP")}件
                          </small>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
